import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { cx } from '@flowus/common/cx';
import { useState, type FC } from 'react';
import { Switch } from '@flowus/common/components/switch';
import { Images } from 'src/image';
import { Button } from '@flowus/common/components';
import { AiCoverStore, setAiCoverStore } from './store';
import { useOnceAnyDay } from '@flowus/common/hooks/use-once-any-day';
import { useOpenModal } from '@flowus/common/next-modal';
import { useOpenAskUpgradeSpace } from 'src/views/main/setting-modal/use-open-ask-upgrade-space';
import { useOpenUpgradeSpace } from 'src/views/main/setting-modal/use-open-setting-modal';
import { OpenSettingFrom } from 'src/views/main/setting-modal/type';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { extractCodeBlocks, getSpaceIconUrl, startGenerateAiCover } from './uitl';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useGetPageId } from 'src/utils/getPageId';
import { message } from '@flowus/common/components/message';
import { getCurrentSpace } from 'src/hooks/space';
// https://cdn2.flowus.cn/public/512d9d30-aa4c-43bc-b3f0-441b0b1a14be/amen.svg
const images = [
  Images.aiCover1,
  Images.aiCover2,
  Images.aiCover3,
  Images.aiCover4,
  Images.aiCover5,
  Images.aiCover6,
];

interface Props {
  showQRCode?: boolean;
  showSpaceInfo?: boolean;
  chatId?: string;
}
export const AICoverAside: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const coverItems: (CoverItemProps & { coverType: string })[] = [
    {
      coverType: '3:4',
      title: t('components.share.ai_cover.size_3_4'),
      description: t('components.share.ai_cover.size_3_4_desc'),
    },
    {
      coverType: 'auto',
      title: t('components.share.ai_cover.size_auto'),
      description: t('components.share.ai_cover.size_auto_desc'),
    },
  ];
  const [openQRCodeSwitch, setOpenQRCodeSwitch] = useState(true);
  const [openSpaceInfoSwitch, setOpenSpaceInfoSwitch] = useState(true);
  const [coverType, setCoverType] = useState(coverItems[0]?.coverType ?? 'auto');
  const html = AiCoverStore((state) => state.html);
  const loading = AiCoverStore((state) => state.loading);
  const { showOnce, close } = useOnceAnyDay('ai-cover-tip-1', {
    endDay: -1,
  });
  const pageId = useGetPageId();
  const openModal = useOpenModal();
  const isProSpace = useIsProSpace();
  const openAskUpgradeSpace = useOpenAskUpgradeSpace();
  const openUpgradeSpace = useOpenUpgradeSpace();

  const requestAiCover = async () => {
    if (loading) return;
    if (!isProSpace) {
      openAskUpgradeSpace(() => {
        openUpgradeSpace(OpenSettingFrom.ai_cover);
      });
      return;
    }
    const space = getCurrentSpace();
    const url = await getSpaceIconUrl(space.icon);
    setAiCoverStore({ loading: true });
    let parsm: any;
    if (props.chatId) {
      parsm = { chatId: props.chatId, spaceId: space.uuid };
    } else {
      parsm = {
        blockId: pageId,
        showQcode: openQRCodeSwitch,
        showSpaceInfo: openSpaceInfoSwitch,
        spaceId: getCurrentSpaceId(),
      };
      if (url) {
        parsm = { ...parsm, spaceIcon: url };
      }
    }
    try {
      await startGenerateAiCover(
        {
          ...parsm,
          ratio: coverType,
        },
        (resp) => {
          if (resp.message) {
            setAiCoverStore({ message: resp.message });
          } else if (resp.markdown) {
            void extractCodeBlocks(resp.markdown).then((res) => {
              if (res.length > 0) {
                setAiCoverStore({ loading: false, html: res[0]?.code });
              }
            });
          } else if (resp.error) {
            message.warning(resp.error);
            setAiCoverStore({ loading: false, message: '' });
          }
        }
      );
    } catch (err) {
      console.log('err: ', err);
      setAiCoverStore({ loading: false, html: '', message: '' });
    }
  };
  return (
    <div className="w-[300px] shrink-0">
      <div className="px-4 mt-4 mb-2 text-t2-medium">
        {t('components.share.ai_cover.poster_size')}
      </div>
      <div className="px-4 flex items-center space-x-4">
        {coverItems.map((item) => {
          return (
            <CoverItem
              key={item.coverType}
              {...item}
              onSelect={() => {
                setCoverType(item.coverType);
              }}
              active={item.coverType === coverType}
            />
          );
        })}
      </div>
      <div
        hidden={props.showQRCode}
        onClick={() => {
          setOpenQRCodeSwitch(!openQRCodeSwitch);
        }}
        className={cx(
          'mt-2 flex justify-between items-center px-4 py-2.5',
          'animate-hover rounded-none animate-click'
        )}
      >
        <div className="text-t2">{t('components.share.ai_cover.show_qr_code')}</div>
        <Switch bgClassName="pointer-events-none" open={openQRCodeSwitch} />
      </div>
      <div
        hidden={props.showQRCode}
        onClick={() => {
          setOpenSpaceInfoSwitch(!openSpaceInfoSwitch);
        }}
        className={cx(
          'flex justify-between items-center px-4 py-2.5',
          'animate-hover rounded-none animate-click'
        )}
      >
        <div className="text-t2">{t('components.share.ai_cover.show_space_info')}</div>
        <Switch bgClassName="pointer-events-none" open={openSpaceInfoSwitch} />
      </div>
      <div className="text-normal text-t2-medium mt-4 ml-4">
        {t('components.share.ai_cover.generation_effect_display')}
      </div>
      <div className="grid grid-cols-2 px-4 mt-2 gap-2 h-[230px] overflow-auto">
        {images.map((item, index) => {
          return <img key={index} className="w-[126px]" src={item} />;
        })}
      </div>
      <div className="px-4">
        <Button
          className="mt-6 mb-5 w-full"
          colorType="active"
          onClick={() => {
            if (html && showOnce) {
              openModal.warning({
                content: (
                  <div className="text-grey1">
                    <div>{t('components.share.ai_cover.regenerate_will_overwrite')}</div>
                    <div>{t('components.share.ai_cover.save_poster_first')}</div>
                  </div>
                ),
                confirmText: t('common.continue'),
                confirm: () => {
                  close();
                  void requestAiCover();
                },
                cancelText: t('common.cancel'),
              });
              return;
            }
            void requestAiCover();
          }}
        >
          {t('common.generate_poster')}
        </Button>
      </div>
    </div>
  );
};

interface CoverItemProps {
  title: string;
  description: string;
  active?: boolean;
  onSelect?: () => void;
}
const CoverItem: FC<CoverItemProps> = (props) => {
  return (
    <div
      className={cx(
        ' cursor-pointer rounded-lg w-[100px] h-[100px] flex flex-col justify-center items-center  border border-grey6',
        {
          'border-normal/85 border-[2px]': props.active,
        }
      )}
      onClick={props.onSelect}
    >
      <div className="w-[30px] h-[40px] bg-aside rounded" />
      <div className="mt-1 text-t4-medium text-normal">{props.title}</div>
      <div className="mt-0.5 text-t4 text-normal">{props.description}</div>
    </div>
  );
};
